import type {
  Eligibility,
  Proof,
  RegisterBody,
  RegisterResult,
  SiteConfig,
  Stats,
} from "./types";

/** Thrown for any non-2xx response so callers can branch on 404 explicitly. */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export const isNotFound = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 404;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      headers: { Accept: "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch {
    throw new ApiError(0, "Could not reach the server. Check your connection and try again.");
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const message =
      payload && typeof payload === "object" && typeof (payload as { error?: unknown }).error === "string"
        ? (payload as { error: string }).error
        : `Request failed with status ${response.status}.`;
    throw new ApiError(response.status, message);
  }

  return payload as T;
}

export const getConfig = (): Promise<SiteConfig> => request<SiteConfig>("/api/config");

export const getStats = (): Promise<Stats> => request<Stats>("/api/stats");

export const getEligibility = (solanaAddress: string): Promise<Eligibility> =>
  request<Eligibility>(`/api/eligibility/${encodeURIComponent(solanaAddress)}`);

export const getProof = (evmAddress: string): Promise<Proof> =>
  request<Proof>(`/api/proof/${encodeURIComponent(evmAddress)}`);

export const postRegister = (body: RegisterBody): Promise<RegisterResult> =>
  request<RegisterResult>("/api/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
